/**
 * One origin or destination box for every mode. Suggestions, in order: the
 * company's saved locations and Google Places matches, then the airports
 * (Flight ticked) and ports (Ocean ticked) that match or sit near the top
 * place. Picking a place finds the airports and ports around it
 * (`places/resolve/`), and each ticked mode searches those. Picking an airport
 * or port by name or code searches exactly that site for its own kind.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AutoComplete } from 'antd';

import { GetSettingsLocationById } from '../../../apis/settings';
import {
  LaneMode,
  LaneSiteMatch,
  ResolveLaneSites,
  SearchLaneSites,
  ResolvedSites,
  unwrapLanePayload,
} from '../../../apis/lanePlanner';
import { useToken } from '../../../common/helper';
import { findAirportByCode, searchStaticAirports } from './airports';
import {
  createPlacesSession,
  fetchPlaceCoordinates,
  fetchPredictions,
  googlePlacesAvailable,
  PlaceCoordinates,
  PlacePrediction,
} from './googlePlaces';
import { searchStaticPorts } from './oceanPorts';
import {
  airportCodeInLabel,
  PlaceValue,
  placeValueFromResolved,
  snapToAirport,
} from './placeValue';
import {
  matchSavedLocations,
  SavedLocation,
  useSavedLocations,
} from './useSavedLocations';
import styles from './styles.module.css';

const LOOKUP_DELAY_MS = 350;
/** Extra pause before the sites near the top match are looked up. */
const PREVIEW_DELAY_MS = 600;
/** Shorter text is only matched against the short lists and saved locations. */
const MIN_SITE_LENGTH = 2;
const MIN_GOOGLE_LENGTH = 3;
const GROUP_LIMIT = 5;
const IATA_RE = /^[A-Za-z]{3}$/;
const UNLOCODE_RE = /^[A-Za-z]{2}[A-Za-z2-9]{3}$/;

type ChoiceKind = 'saved' | 'google' | 'airport' | 'port';

interface Choice {
  kind: ChoiceKind;
  /** Saved location id, Google place id, or IATA / UN-LOCODE code. */
  id: string;
  /** What the box shows once picked. */
  text: string;
  /** Second line in the list (address, country, distance). */
  detail?: string;
  /** The street address a Road search uses (a saved location's address). */
  address?: string;
  /** A Google place that is an airport. */
  airport?: boolean;
}

interface Suggestions {
  places: Choice[];
  airports: Choice[];
  ports: Choice[];
}

const EMPTY: Suggestions = { places: [], airports: [], ports: [] };

const kmText = (km: number): string => `${Math.round(km)} km`;

/** An airport or port as a list choice: "LAX — Los Angeles International", with where it is. */
const siteToChoice = (
  kind: 'airport' | 'port',
  site: {
    code: string;
    name: string;
    city?: string;
    country: string;
    distance_km?: number;
  }
): Choice => ({
  kind,
  id: site.code,
  text: `${site.code} — ${site.name}`,
  detail: [
    site.city,
    site.country,
    site.distance_km === undefined ? '' : kmText(site.distance_km),
  ]
    .filter(Boolean)
    .join(' · '),
});

const dedupe = (choices: Choice[]): Choice[] => {
  const seen = new Set<string>();
  return choices.filter((choice) => {
    const key = `${choice.kind}:${choice.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const staticAirports = (text: string): Choice[] =>
  searchStaticAirports(text, GROUP_LIMIT).map((a) =>
    siteToChoice('airport', a)
  );

const staticPorts = (text: string): Choice[] =>
  searchStaticPorts(text, GROUP_LIMIT).map((p) => siteToChoice('port', p));

const matchChoice = (match: LaneSiteMatch): Choice =>
  siteToChoice(match.kind, match);

const savedChoice = (location: SavedLocation): Choice => ({
  kind: 'saved',
  id: location.id,
  text: location.name || location.address,
  detail: location.name ? location.address : undefined,
  address: location.address || undefined,
});

interface PlaceSearchBoxProps {
  /** Injected by Form.Item */
  value?: PlaceValue;
  onChange?: (value: PlaceValue | undefined) => void;
  onBlur?: React.FocusEventHandler;
  id?: string;
  modes: LaneMode[];
  placeholder?: string;
}

const PlaceSearchBox: React.FC<PlaceSearchBoxProps> = ({
  value,
  onChange,
  onBlur,
  id,
  modes,
  placeholder,
}) => {
  // the API functions ignore the argument (the interceptor reads the session token itself)
  const { token: sessionToken } = useToken();
  const token = sessionToken ?? '';
  const saved = useSavedLocations(token);
  const [text, setText] = useState(value?.label ?? '');
  const [suggestions, setSuggestions] = useState<Suggestions>(EMPTY);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({ airports: false, ports: false });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchGen = useRef(0);
  const pickGen = useRef(0);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previews = useRef(new Map<string, ResolvedSites>());
  // what the server found for the text now in the box, for a code typed in full
  const siteHits = useRef<Pick<Suggestions, 'airports' | 'ports'>>(EMPTY);
  // the text (upper case) whose server lookup has finished, hits or not
  const siteHitsFor = useRef('');
  // this box's Places billing session (Origin and Destination each have one)
  const placesSession = useRef(createPlacesSession()).current;

  const flight = modes.includes('air');
  const ocean = modes.includes('ocean');
  // with one of Flight and Ocean ticked, the server need only search that kind
  const siteKind = flight !== ocean ? (flight ? 'airport' : 'port') : undefined;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (previewTimer.current) clearTimeout(previewTimer.current);
      searchGen.current += 1;
      pickGen.current += 1;
    },
    []
  );

  // A value arriving from outside (a saved search, a pick) is shown as it is.
  // Clearing is the form remounting the box (LaneSearchForm), so typing that
  // was never picked does not need to be undone here.
  useEffect(() => {
    if (value) setText(value.label);
  }, [value]);

  const localSuggestions = (query: string): Suggestions => ({
    places: matchSavedLocations(saved, query, GROUP_LIMIT).map(savedChoice),
    airports: flight ? staticAirports(query) : [],
    ports: ocean ? staticPorts(query) : [],
  });

  const handleSearch = (input: string): void => {
    pickGen.current += 1;
    setText(input);
    setOpen(true);
    if (value) onChange?.(undefined);
    if (timer.current) clearTimeout(timer.current);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const gen = ++searchGen.current;
    const query = input.trim();
    if (!query) {
      setSuggestions(EMPTY);
      return;
    }
    const local = localSuggestions(query);
    const found = { airports: [] as Choice[], ports: [] as Choice[] };
    siteHits.current = found;
    siteHitsFor.current = '';
    let google: Choice[] = [];
    let near: Pick<Suggestions, 'airports' | 'ports'> = EMPTY;
    // Best first: what the text names, then what is near the top place, then the short lists.
    const show = (): void =>
      setSuggestions({
        places: dedupe([...local.places, ...google]),
        airports: flight
          ? dedupe([
              ...found.airports,
              ...near.airports,
              ...local.airports,
            ]).slice(0, GROUP_LIMIT)
          : [],
        ports: ocean
          ? dedupe([...found.ports, ...near.ports, ...local.ports]).slice(
              0,
              GROUP_LIMIT
            )
          : [],
      });
    setSuggestions(local);
    if (query.length < MIN_SITE_LENGTH) return;
    timer.current = setTimeout(() => {
      // every bundled airport and port by code, name or city, not just the short lists
      const _sites = (async () => {
        if (!flight && !ocean) return;
        try {
          const matches = unwrapLanePayload<LaneSiteMatch[]>(
            await SearchLaneSites(token, query, siteKind)
          );
          if (gen !== searchGen.current) return;
          found.airports = matches
            .filter((m) => m.kind === 'airport')
            .map(matchChoice);
          found.ports = matches
            .filter((m) => m.kind === 'port')
            .map(matchChoice);
          siteHitsFor.current = query.toUpperCase();
          show();
        } catch {
          /* the short lists still answer */
        }
      })();
      if (query.length < MIN_GOOGLE_LENGTH) return;
      const _lookup = (async () => {
        let predictions: PlacePrediction[] = [];
        try {
          predictions = await fetchPredictions(query, placesSession);
        } catch {
          /* no Google: saved locations and codes still work */
        }
        if (gen !== searchGen.current || predictions.length === 0) return;
        google = predictions.slice(0, GROUP_LIMIT).map((p) => ({
          kind: 'google',
          id: p.id,
          text: p.label,
          airport: p.airport,
        }));
        show();
        // The sites near the top match, as Google Flights lists a city's airports.
        // Looked up only once typing pauses, and kept per place, so keystrokes
        // that get overwritten cost no place-details or resolve call.
        previewTimer.current = setTimeout(() => {
          const _preview = (async () => {
            try {
              const topId = predictions[0].id;
              let resolved = previews.current.get(topId);
              if (!resolved) {
                const at = await fetchPlaceCoordinates(topId);
                resolved = unwrapLanePayload<ResolvedSites>(
                  await ResolveLaneSites(token, at)
                );
                previews.current.set(topId, resolved);
              }
              if (gen !== searchGen.current) return;
              near = {
                airports: resolved.airports
                  .filter((s) => s.default)
                  .map((s) => siteToChoice('airport', s)),
                ports: resolved.ports
                  .filter((s) => s.default)
                  .map((s) => siteToChoice('port', s)),
              };
              show();
            } catch {
              /* the list keeps the matches it has */
            }
          })();
        }, PREVIEW_DELAY_MS);
      })();
    }, LOOKUP_DELAY_MS);
  };

  const coordinatesOf = async (choice: Choice): Promise<PlaceCoordinates> => {
    if (choice.kind === 'google') {
      return fetchPlaceCoordinates(choice.id, { session: placesSession });
    }
    const res = await GetSettingsLocationById(token, choice.id);
    const location = res?.data?.data?.data;
    const lat = Number(location?.latitude);
    const lng = Number(location?.longitude);
    if (
      location?.latitude == null ||
      location?.longitude == null ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      throw new Error('This saved location has no coordinates.');
    }
    return { lat, lng };
  };

  const pick = async (choice: Choice): Promise<void> => {
    const gen = ++pickGen.current;
    searchGen.current += 1;
    if (timer.current) clearTimeout(timer.current);
    // a top-match preview still waiting is for text the user has now left
    if (previewTimer.current) clearTimeout(previewTimer.current);
    setText(choice.text);
    setSuggestions(EMPTY);
    setOpen(false);
    // a pick that is not a Google place never sends details: its typing session is dropped
    if (choice.kind !== 'google') placesSession.end();
    const pickedSite =
      choice.kind === 'airport' || choice.kind === 'port'
        ? choice.kind
        : undefined;
    const pickKind = pickedSite ?? 'place';
    onChange?.({
      label: choice.text,
      kind: pickKind,
      ...(choice.address ? { address: choice.address } : {}),
      airports: [],
      ports: [],
      resolving: true,
    });
    try {
      let resolved: ResolvedSites;
      let at: PlaceCoordinates | undefined;
      let resolvedKind: PlaceValue['kind'] = pickKind;
      if (pickedSite) {
        resolved = unwrapLanePayload<ResolvedSites>(
          await ResolveLaneSites(token, { code: choice.id })
        );
      } else {
        at = await coordinatesOf(choice);
        resolved = unwrapLanePayload<ResolvedSites>(
          await ResolveLaneSites(token, at)
        );
        // an airport found on Google is that one airport, as picking it by code is
        const snapped = snapToAirport(resolved, {
          code: airportCodeInLabel(choice.text),
          flagged: Boolean(choice.airport),
        });
        if (snapped) {
          resolved = snapped;
          resolvedKind = 'airport';
        }
      }
      if (gen !== pickGen.current) return;
      onChange?.(
        placeValueFromResolved(
          choice.text,
          resolved,
          at,
          resolvedKind,
          choice.kind === 'saved'
            ? choice.address
            : choice.kind === 'google'
              ? choice.text
              : undefined
        )
      );
    } catch (err) {
      if (gen !== pickGen.current) return;
      onChange?.({
        label: choice.text,
        kind: pickKind,
        airports: [],
        ports: [],
        error:
          err instanceof Error && err.message.includes('no coordinates')
            ? err.message
            : 'Could not find airports and ports near this place. Try another pick.',
      });
    }
  };

  const handleSelect = (key: string): void => {
    const all = [
      ...suggestions.places,
      ...suggestions.airports,
      ...suggestions.ports,
    ];
    const choice = all.find((c) => `${c.kind}:${c.id}` === key);
    if (choice) {
      const _ = pick(choice);
    }
  };

  // A code typed in full counts as a pick, so "JFK" or "NLRTM" need no click.
  // Not when a place is on offer for the same text ("Ulm" is a city before it
  // is an airport code): the list's suggestions or, if they have not arrived
  // yet, Google's, so the user picks from the list then.
  const handleBlur = (event: React.FocusEvent): void => {
    setOpen(false);
    if (value) {
      onBlur?.(event);
      return;
    }
    const code = text.trim().toUpperCase();
    const airportCode = flight && IATA_RE.test(code);
    const portCode = ocean && UNLOCODE_RE.test(code);
    if (!airportCode && !portCode) {
      onBlur?.(event);
      return;
    }
    const hit = (kind: 'airports' | 'ports'): Choice | undefined =>
      siteHits.current[kind].find((c) => c.id === code);
    const staticAirport = findAirportByCode(code);
    const staticPort = searchStaticPorts(code, 1)[0];
    const knownAirport =
      airportCode &&
      (hit('airports') ??
        (staticAirport && siteToChoice('airport', staticAirport)));
    const knownPort =
      portCode &&
      (hit('ports') ??
        (staticPort?.code === code
          ? siteToChoice('port', staticPort)
          : undefined));
    // the debounced lookup has not answered for this text yet (blurred within its delay)
    const needsLookup =
      !knownAirport && !knownPort && siteHitsFor.current !== code;
    if (!knownAirport && !knownPort && !needsLookup) {
      onBlur?.(event);
      return;
    }
    // Mark the box as busy first, so validating on blur does not flash "Pick a
    // place" while the check below runs; it is undone if a place is on offer.
    const gen = pickGen.current;
    onChange?.({
      label: text,
      kind: airportCode && (knownAirport || !knownPort) ? 'airport' : 'port',
      airports: [],
      ports: [],
      resolving: true,
    });
    onBlur?.(event);
    const _blur = (async () => {
      let airport = knownAirport || undefined;
      let port = knownPort || undefined;
      if (needsLookup) {
        try {
          const matches = unwrapLanePayload<LaneSiteMatch[]>(
            await SearchLaneSites(token, code, siteKind)
          );
          const exact = (kind: LaneSiteMatch['kind']): Choice | undefined =>
            matches
              .filter((m) => m.kind === kind && m.code === code)
              .map(matchChoice)[0];
          airport = airportCode ? exact('airport') : undefined;
          port = portCode ? exact('port') : undefined;
        } catch {
          /* unknown, as when the server cannot be reached */
        }
        if (gen !== pickGen.current) return;
        if (!airport && !port) {
          onChange?.(undefined);
          return;
        }
      }
      const offered =
        suggestions.places.length > 0 ||
        (googlePlacesAvailable() &&
          (
            await fetchPredictions(code, placesSession).catch(
              (): PlacePrediction[] => []
            )
          ).length > 0);
      if (gen !== pickGen.current) return;
      if (offered) {
        onChange?.(undefined);
        return;
      }
      const chosen = airport ?? port;
      if (chosen) {
        const _ = pick(chosen);
      }
    })();
  };

  const toggleGroup = (group: 'airports' | 'ports'): void =>
    setCollapsed((current) => ({ ...current, [group]: !current[group] }));

  const header = (
    title: string,
    group: 'airports' | 'ports'
  ): React.ReactNode => (
    <button
      type='button'
      className={styles.placeGroupHeader}
      aria-expanded={!collapsed[group]}
      onMouseDown={(e) => {
        e.preventDefault(); // keep the input focused and the list open
        toggleGroup(group);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleGroup(group);
        }
      }}
      tabIndex={-1} // Tab moves on to the next field; the keyboard toggles groups from the input (Alt+Up / Alt+Down)
      aria-keyshortcuts='Alt+ArrowUp Alt+ArrowDown'
    >
      {title}
      <span aria-hidden>{collapsed[group] ? '⌄' : '⌃'}</span>
    </button>
  );

  const option = (choice: Choice): { value: string; label: string } => ({
    value: `${choice.kind}:${choice.id}`,
    label: choice.text,
  });

  const options = [
    ...suggestions.places.map(option),
    ...(flight && suggestions.airports.length
      ? [
          {
            label: header('Airports', 'airports'),
            options: collapsed.airports ? [] : suggestions.airports.map(option),
          },
        ]
      : []),
    ...(ocean && suggestions.ports.length
      ? [
          {
            label: header('Ports', 'ports'),
            options: collapsed.ports ? [] : suggestions.ports.map(option),
          },
        ]
      : []),
  ];

  const ready = Boolean(value && !value.resolving && !value.error);
  const summary = value
    ? [
        flight &&
          `${value.airports.filter((a) => a.default).length} airport(s)`,
        ocean && `${value.ports.filter((p) => p.default).length} port(s)`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  // always mounted, so a screen reader announces each change of state
  const status = value?.resolving
    ? 'Finding nearby airports and ports…'
    : (value?.error ?? summary);
  const hintId = `${id ?? 'place'}-hint`;
  const detailOf = (key: string): string | undefined =>
    [...suggestions.places, ...suggestions.airports, ...suggestions.ports].find(
      (c) => `${c.kind}:${c.id}` === key
    )?.detail;

  return (
    <div className={styles.placeBox}>
      <AutoComplete
        id={id}
        value={text}
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        open={open && options.length > 0}
        onOpenChange={setOpen}
        onBlur={handleBlur}
        aria-describedby={hintId}
        onKeyDown={(e) => {
          // keyboard way to fold the Airports / Ports groups
          if (e.altKey && e.key === 'ArrowUp') {
            e.preventDefault();
            setCollapsed({ airports: true, ports: true });
          } else if (e.altKey && e.key === 'ArrowDown') {
            e.preventDefault();
            setCollapsed({ airports: false, ports: false });
          }
        }}
        placeholder={placeholder}
        variant='borderless'
        style={{ width: '100%', height: '100%' }}
        popupMatchSelectWidth
        optionRender={(item) => (
          <span className={styles.placeOption}>
            <span className={styles.placeOptionText}>{item.label}</span>
            {detailOf(String(item.value)) && (
              <span className={styles.placeOptionDetail}>
                {detailOf(String(item.value))}
              </span>
            )}
          </span>
        )}
      />
      <span id={hintId} className={styles.srOnly}>
        Alt plus Up or Down arrow folds or unfolds the Airports and Ports
        groups.
      </span>
      <div
        role='status'
        aria-live='polite'
        className={ready ? styles.placeSummary : styles.srOnly}
      >
        {status}
      </div>
    </div>
  );
};

export default PlaceSearchBox;
